import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SeoHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  schemaData?: Record<string, unknown>[];
  isArticle?: boolean;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
}

export default function SeoHead({
  title,
  description,
  keywords,
  canonicalUrl = 'https://8866h.cc',
  ogType = 'website',
  ogImage = '',
  schemaData = [],
  isArticle = false,
  articlePublishedTime = '',
  articleModifiedTime = '',
}: SeoHeadProps) {
  const location = useLocation();

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', `https://8866h.cc${location.pathname}`, true);
    if (ogImage) setMeta('og:image', ogImage, true);
    setMeta('og:site_name', '8866h.cc', true);
    setMeta('og:locale', 'zh_CN', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    if (ogImage) setMeta('twitter:image', ogImage);

    const existingLink = document.querySelector('link[rel="canonical"]');
    if (existingLink) existingLink.remove();
    const link = document.createElement('link');
    link.rel = 'canonical';
    link.href = canonicalUrl;
    document.head.appendChild(link);

    if (isArticle) {
      if (articlePublishedTime) setMeta('article:published_time', articlePublishedTime);
      if (articleModifiedTime) setMeta('article:modified_time', articleModifiedTime);
    }

    const existingSchemas = document.querySelectorAll('script[type="application/ld+json"][data-seo="true"]');
    existingSchemas.forEach((s) => s.remove());

    schemaData.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', 'true');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, schemaData, isArticle, articlePublishedTime, articleModifiedTime, location.pathname]);

  return null;
}